import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { ChartBar, MagnifyingGlass } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// dd/mm/yyyy, HH:mm:ss - user's explicit ask ("indian format dd mm yyyy
// and time"). Uses the browser's own local timezone (en-GB just gives
// the day-first ordering) rather than hardcoding IST, since the SAP
// timestamp is UTC and every admin viewing this is already on IST.
function formatIndianDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).replace(",", "");
}

// Oct 2026, user's explicit ask - "Product Allocation" report: every open
// Site Logistics Task line at a site, showing which document is holding
// that allocation, so contention on a shared logistics area (like the
// STO-000698 root cause: P9-HOLD's Available Stock going negative for
// SCR630WM) is visible at a glance instead of hunting SAP's Task Control.
export default function ProductAllocationPage() {
  const [sites, setSites] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [productId, setProductId] = useState("");
  const [logisticsAreaId, setLogisticsAreaId] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/product-allocations/sites`).then(({ data }) => {
      setSites(data.sites || []);
      setDocumentTypes(data.document_types || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLogisticsAreaId("");
    if (!siteId) {
      setWarehouses([]);
      return;
    }
    axios.get(`${API}/product-allocations/warehouses/${siteId}`).then(({ data }) => {
      setWarehouses(data.warehouses || []);
    }).catch(() => setWarehouses([]));
  }, [siteId]);

  const search = async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    setRows(null);
    try {
      const params = { site_id: siteId };
      if (productId.trim()) params.product_id = productId.trim();
      if (logisticsAreaId) params.logistics_area_id = logisticsAreaId;
      if (documentType) params.document_type = documentType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/product-allocations`, { params });
      setRows(data.rows || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load allocations for this site.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <ChartBar size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="product-allocation-title">
            Product Allocation
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            See every open warehouse task holding stock for a product, and spot contention on a shared logistics area.
          </p>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-md p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Site *</label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="w-36" data-testid="product-allocation-site-filter">
                <SelectValue placeholder="Select site" />
              </SelectTrigger>
              <SelectContent>
                {sites.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Product ID</label>
            <Input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="e.g. SCR630WM" className="w-40" data-testid="product-allocation-product-filter" />
          </div>
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Logistics Area</label>
            <Select value={logisticsAreaId} onValueChange={(v) => setLogisticsAreaId(v === "any" ? "" : v)} disabled={!siteId}>
              <SelectTrigger className="w-44" data-testid="product-allocation-area-filter">
                <SelectValue placeholder={siteId ? "Any logistics area" : "Select site first"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any logistics area</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Document Type</label>
            <Select value={documentType} onValueChange={(v) => setDocumentType(v === "any" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="product-allocation-document-type-filter">
                <SelectValue placeholder="Any document type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any document type</SelectItem>
                {documentTypes.map((dt) => (
                  <SelectItem key={dt.code} value={dt.code}>{dt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Planned From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="product-allocation-date-from" />
          </div>
          <div>
            <label className="text-xs text-[#667085] font-medium block mb-1">Planned To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="product-allocation-date-to" />
          </div>
          <Button onClick={search} disabled={loading || !siteId} data-testid="product-allocation-search-button" className="gap-1.5">
            <MagnifyingGlass size={14} weight="bold" />
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>

        {error && (
          <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-6 text-center text-sm text-[#667085]" data-testid="product-allocation-error">
            {error}
          </div>
        )}

        {loading && (
          <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-10 text-center text-sm text-[#667085]" data-testid="product-allocation-loading">
            Querying SAP for open allocations at {siteId}... this can take up to 20s.
          </div>
        )}

        {rows && (
          <div className="bg-white border border-[#D0D5DD] rounded-md overflow-x-auto" data-testid="product-allocation-result">
            {rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#667085]" data-testid="product-allocation-empty">
                No open allocations found for these filters.
              </div>
            ) : (
              <table className="w-full text-xs min-w-[1350px]">
                <thead>
                  <tr className="bg-[#F9FAFB] text-[#667085] text-xs uppercase tracking-wide">
                    <th className="text-left px-3 py-2">Planned Date</th>
                    <th className="text-left px-3 py-2">Document Type</th>
                    <th className="text-left px-3 py-2">Reference</th>
                    <th className="text-left px-3 py-2">Warehouse Task ID</th>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-left px-3 py-2">Movement</th>
                    <th className="text-left px-3 py-2">Ship From</th>
                    <th className="text-left px-3 py-2">Ship To</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">Logistics Area</th>
                    <th className="text-left px-3 py-2">Site</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-[#EAECF0] hover:bg-[#F9FAFB]" data-testid={`product-allocation-row-${i}`}>
                      <td className="px-3 py-2 text-[#1D2939] whitespace-nowrap">{formatIndianDateTime(r.planned_date)}</td>
                      <td className="px-3 py-2 text-[#1D2939]">{r.document_type}</td>
                      <td className="px-3 py-2 text-[#1D2939] font-medium">{r.reference || "-"}</td>
                      <td className="px-3 py-2 text-[#0E7C86] font-semibold">{r.task_id || "-"}</td>
                      <td className="px-3 py-2 text-[#1D2939]">{r.product_id}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${r.side === "from" ? "bg-[#FEF3F2] text-[#B42318]" : "bg-[#ECFDF3] text-[#027A48]"}`}>
                          {r.side === "from" ? "Pulling From" : "Arriving Into"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#667085]">{r.ship_from_site_id || "-"}</td>
                      <td className="px-3 py-2 text-[#667085]">{r.ship_to_site_id || "-"}</td>
                      <td className="px-3 py-2 text-right text-[#1D2939] font-medium">{r.qty != null ? `${r.qty} ${r.unit_code}` : "-"}</td>
                      <td className="px-3 py-2 text-[#B54708] font-semibold">{r.logistics_area_id || "-"}</td>
                      <td className="px-3 py-2 text-[#667085]">{r.site_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
